<template>
  <div class="container">
    <div class="center">
      <div class="reset">
        <form @submit.prevent="handleResetPassword">
          <div class="form">
            <div class="header">
              <h2>Reset Password</h2>
            </div>

            <!-- ✅ Success Message -->
            <div v-if="successMessage" class="alert alert-success">
              {{ successMessage }}
              <p class="success-note">You can now return to the app.</p>
            </div>

            <!-- ❌ Error Message -->
            <div v-if="errorMessage" class="alert alert-error">
              {{ errorMessage }}
            </div>

            <!-- ❌ Invalid Token -->
            <div v-if="invalidToken" class="alert alert-error">
              {{ invalidToken }}
            </div>

            <!-- 🔒 Password Fields (hidden when successful or invalid) -->
            <div class="content" v-if="!successMessage && !invalidToken">
              <!-- New Password -->
              <div class="relative newPass">
                <input
                  :type="showNewPass ? 'text' : 'password'"
                  v-model="pass"
                  placeholder="New Password"
                  required
                  :disabled="loading"
                  class="password-input"
                />
                <button
                  type="button"
                  class="eye-btn"
                  @click="showNewPass = !showNewPass"
                  :aria-label="showNewPass ? 'Hide password' : 'Show password'"
                >
                  <PhEye v-if="showNewPass" :size="22" />
                  <PhEyeSlash v-else :size="22" />
                </button>
              </div>

              <!-- Confirm Password -->
              <div class="relative confirmPass">
                <input
                  :type="showConfirmPass ? 'text' : 'password'"
                  v-model="confirmPassword"
                  placeholder="Confirm Password"
                  required
                  :disabled="loading"
                  class="password-input"
                />
                <button
                  type="button"
                  class="eye-btn"
                  @click="showConfirmPass = !showConfirmPass"
                  :aria-label="
                    showConfirmPass
                      ? 'Hide confirm password'
                      : 'Show confirm password'
                  "
                >
                  <PhEye v-if="showConfirmPass" :size="22" />
                  <PhEyeSlash v-else :size="22" />
                </button>
              </div>
            </div>

            <!-- 🔘 Reset Button -->
            <div class="button" v-if="!successMessage && !invalidToken">
              <button
                type="submit"
                :disabled="loading || !isFormValid"
                class="submit-btn"
              >
                <span v-if="loading">Resetting...</span>
                <span v-else>Reset Password</span>
              </button>
            </div>

            <!-- 🔙 Return Button -->
            <div class="back-to-login" v-if="invalidToken">
              <button type="button" @click="goToApp" class="back-btn">
                Return to App
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
// import { useRouter } from "vue-router";
import { PhEye, PhEyeSlash } from "@phosphor-icons/vue"; // same as Register.vue

// const router = useRouter();

const pass = ref("");
const confirmPassword = ref("");
const loading = ref(false);
const successMessage = ref("");
const errorMessage = ref("");
const invalidToken = ref("");
const resetToken = ref("");

const showNewPass = ref(false);
const showConfirmPass = ref(false);

// ✅ Validate password match and minimum length
const isFormValid = computed(() => {
  return (
    pass.value &&
    confirmPassword.value &&
    pass.value === confirmPassword.value &&
    pass.value.length >= 6
  );
});

// Extract token from URL
const extractTokenFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  resetToken.value = urlParams.get("token");

  if (!resetToken.value) {
    invalidToken.value =
      "Invalid or missing reset token. Please request a new password reset link.";
  }
};

// Handle reset password
const handleResetPassword = async () => {
  if (!resetToken.value) {
    extractTokenFromURL();
    if (!resetToken.value) {
      invalidToken.value = "Reset token is missing or invalid";
      return;
    }
  }

  if (pass.value !== confirmPassword.value) {
    errorMessage.value = "Passwords do not match";
    return;
  }

  if (pass.value.length < 6) {
    errorMessage.value = "Password must be at least 6 characters long";
    return;
  }

  loading.value = true;
  errorMessage.value = "";
  invalidToken.value = "";
  successMessage.value = "";

  try {
    const response = await fetch(
      "https://mobile.api.futurerent.co.za/api/auth/reset-password",
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Session: resetToken.value,
        },
        body: JSON.stringify({
          password: pass.value,
          confirmPsw: confirmPassword.value,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      errorMessage.value = data.message || "Failed to reset password";
      return;
    }

    // ✅ Success message only
    successMessage.value =
      data.message ||
      "Password reset successfully! You can now login with your new password.";

    pass.value = "";
    confirmPassword.value = "";

    // Prevent going back
    window.history.replaceState(null, null, window.location.href);
  } catch (error) {
    errorMessage.value = "An unexpected error occurred. Please try again.";
    console.error("Reset password error:", error);
  } finally {
    loading.value = false;
  }
};

// Navigate manually if needed
// const goToApp = () => {
//   router.push("/app");
// };

onMounted(() => {
  extractTokenFromURL();
});
</script>

<style scoped>
/* === Layout === */
.container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background:
    linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)),
    url("@/assets/1733923787514.jpeg");
  background-size: cover;
  background-position: center;
  font-family: "Poppins", sans-serif;
}

/* === Form Box === */
.center {
  width: fit-content;
  padding: 30px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.relative {
  position: relative;
  width: 100%;
}

.header h2 {
  text-align: center;
  color: #333;
  margin-bottom: 25px;
  font-size: 1.8rem;
  font-weight: 600;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
}
.newPass {
  width: 100%;
}
.confirmPass {
  width: 100%;
}
.password-input {
  width: 88%;
  border-radius: 8px;
  padding: 0.5rem 2.5rem 0.5rem 0.5rem;
  border: none;
  box-shadow: 0 0 0 2px rgba(38, 38, 47, 0.114);
}

.password-input:focus {
  border-color: #6c63ff;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.2);
  outline: none;
}

.password-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

/* 👁️ Eye button inside input */
.eye-btn {
  position: absolute;
  inset-y: 0;
  top: 5px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.eye-btn:hover {
  color: #6c63ff;
}
/* === Inputs === */
.form {
  width: 400px;
  padding: 12px 15px;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

/* .newPass input:focus {
  border-color: #6c63ff;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.2);
  outline: none;
}
.confirmPass input:focus {
  border-color: #6c63ff;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.2);
  outline: none;
}

.newPass input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}
.confirmPass input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.eye-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #333;
  transition: color 0.2s ease;
}

.eye-icon:hover {
  color: #6c63ff;
} */

/* === Buttons === */
.button {
  text-align: center;
  margin-top: 10px;
}

.submit-btn {
  width: 100%;
  padding: 12px;
  border: none;
  background: rgba(31, 40, 51, 1);
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
  background: rgba(20, 10, 41, 0.5);
  transform: translateY(-1px);
}

.submit-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

/* === Alerts === */
.alert {
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
}

.alert-success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.alert-error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* === Success note === */
.success-note {
  margin-top: 10px;
  font-size: 0.95rem;
  opacity: 0.85;
  text-align: center;
}

/* === Responsive === */
@media (max-width: 480px) {
  .container {
    padding: 2rem;
  }
  .reset {
    width: fit-content;
  }
  .form {
    width: 300px;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;
  }
  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
  }
  .center {
    width: fit-content;
    padding: 0 !important;
  }
  .header h2 {
    font-size: 1.5rem;
  }
  .newPass {
    width: 100%;
  }
  .confirmPass {
    width: 100%;
  }
  .newPass input {
    width: 94.5%;
    border-radius: 8px;
    padding: 0.5rem;
    border: none;
    box-shadow: 0 0 0 2px rgba(38, 38, 47, 0.114);
  }
  .confirmPass input {
    width: 94.5%;
    border-radius: 8px;
    padding: 0.5rem;
    border: none;
    box-shadow: 0 0 0 2px rgba(38, 38, 47, 0.114);
  }
}
</style>
